import axios from "axios";
import axiosInstance from "../../../../../redux/api";

// The data team picks SKU-named images in the dashboard; the browser uploads
// each one straight to an S3 `incoming/` folder via a presigned URL. The
// backend then matches every file to a live product by SKU and attaches it as
// the cover in one background job. The dashboard owns the full flow:
// upload-url → PUT to S3 → start → poll.
const ENDPOINT = "admin/products/import-images";

/**
 * Ask the backend for a presigned S3 URL for one file. The backend validates
 * the SKU filename + type here and 400s with a `message` on bad input.
 * @returns {Promise<{ upload_url: string, key: string }>}
 */
export const getUploadUrl = async (fileName, fileType) => {
  const { data } = await axiosInstance.post(`${ENDPOINT}/upload-url`, {
    file_name: fileName,
    file_type: fileType,
  });
  return data.response;
};

/**
 * PUT the raw file bytes directly to S3. Uses a bare axios call — NOT our
 * axiosInstance — because the presigned URL carries its own signature and an
 * `Authorization` header (or our JSON Content-Type) would break it. The
 * `Content-Type` MUST equal the `file_type` sent to getUploadUrl.
 */
export const uploadFileToS3 = (uploadUrl, file, fileType, onProgress) =>
  axios.put(uploadUrl, file, {
    headers: { "Content-Type": fileType },
    transformRequest: [(data) => data], // send the File as-is, no JSON transform
    onUploadProgress: (event) => {
      if (onProgress && event.total) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    },
  });

/**
 * Start the background import once all files are in S3.
 * @param {boolean} overwrite — replace photos on products that already have one.
 *   Only a literal `true` enables replace; otherwise the flag is omitted.
 * @returns {Promise<{ task_id: string, status: string }>}
 */
export const startImageImport = async (overwrite) => {
  const body = overwrite === true ? { overwrite: true } : {};
  const { data } = await axiosInstance.post(ENDPOINT, body);
  return data.response;
};

/**
 * Poll the job. `state` is one of PENDING | STARTED | PROGRESS | SUCCESS |
 * FAILURE, with `progress`, `summary`, or `error` attached depending on state.
 * @returns {Promise<{ task_id: string, state: string, progress?: object, summary?: object, error?: string }>}
 */
export const getImageImportStatus = async (taskId) => {
  const { data } = await axiosInstance.get(`${ENDPOINT}/${taskId}`);
  return data.response;
};
